import { useRef, useState, useEffect } from 'react'
import Add from '@/assets/Add'
import Clipboard from '@/assets/Clipboard'
import { Cross2Icon } from '@radix-ui/react-icons'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { convertFileToBase64 } from '@/lib/utils'
import { processExcelFile } from '@/lib/processQueries'
import { useMutation } from '@apollo/client'
import { Label } from '@/components/ui/label'
import Tooltip from '@/components/compliance-assistant/Tooltip'
import Upload from '@/assets/Upload'

export default function CreateModal({
  processData,
  handleGetTemplate,
  setProcessData,
  enrichmentType,
  isViewMode,
}: {
  processData: FormProcessData
  handleGetTemplate: () => void
  setProcessData: React.Dispatch<React.SetStateAction<FormProcessData>>
  enrichmentType: number | undefined
  isViewMode: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [processExcel] = useMutation(processExcelFile)
  const [open, setOpen] = useState(false)

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    setProcessData((prev) => ({
      ...prev,
      file: file,
    }))
  }

  useEffect(() => {
    // Reset file when dialog is closed.
    if (!open) {
      setProcessData((prev) => ({
        ...prev,
        file: undefined,
      }))
    }
  }, [open])

  const handleLoadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    const file = e.target.files?.[0]
    if (file) {
      setProcessData((prev) => ({
        ...prev,
        file: file,
      }))
    }
    e.target.value = '' // Reset input value to allow uploading the same file
  }

  const handleUploadFile = () => {
    if (processData.file === undefined) return
    convertFileToBase64(processData.file).then((file: FileData) => {
      processExcel({
        variables: {
          file64: file.base64String,
          enrichmentTypeId: enrichmentType,
        },
      }).then((res) => {
        const data = res.data?.processExcelFile
        setProcessData((prev) => ({
          ...prev,
          ruts_a: data?.rutsA || [],
          ruts_b: data?.rutsB || [],
        }))
      })
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger disabled={!enrichmentType || isViewMode} asChild>
        <Tooltip content="Cargar archivo">
          <Button
            className="h-6 w-6 px-2"
            size="icon"
            type="button"
            disabled={!enrichmentType || isViewMode}
            onClick={() => setOpen(true)}
          >
            <Add color="#FFFFFF" />
          </Button>
        </Tooltip>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] bg-white">
        <DialogHeader>
          <DialogTitle>Cargar archivo</DialogTitle>
          <DialogDescription>
            A continuación podrás cargar un archivo que contenga los datos de las personas naturales
            o jurídicas que deseas consultar.{' '}
            <Button variant="link" className="p-0" onClick={handleGetTemplate}>
              <u>Descarga aquí la plantilla disponible.</u>
            </Button>
          </DialogDescription>
        </DialogHeader>
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer"
          onDrop={handleFileDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => inputRef.current?.click()}
        >
          <Upload />
          <p className="text-black font-semibold text-lg mt-4">
            Arrastra y suelta aquí tu documento
          </p>
          <p className="text-black font-semibold text-lg mt-4">o</p>
          <Button type="button" variant="outline" className="mt-2">
            Buscar documento
          </Button>
          <p className="text-gray-400 text-xs mt-8">Puede agregar sólo formato xlsx</p>
          <Input
            type="file"
            className="hidden"
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleLoadFile}
            ref={inputRef}
          />
        </div>
        {processData.file && (
          <div className="mt-0">
            <p className="text-gray-500">
              <Label>Archivo cargado</Label>
            </p>
            <div className="flex items-center mt-2">
              <span className="pr-2">
                <Clipboard />
              </span>
              <u>{processData.file.name}</u>
              <Button
                className="w-4 h-4 ml-2 p-1 bg-gray-300 rounded-full opacity-70 hover:opacity-100"
                variant="ghost"
                size="icon"
                onClick={() => setProcessData((prev) => ({ ...prev, file: undefined }))}
              >
                <Cross2Icon className="w-2 h-2" />
              </Button>
            </div>
          </div>
        )}
        <DialogFooter>
          <div className="mt-4 flex justify-end space-x-2">
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                className="w-32 px-4 py-3 text-base text-center"
              >
                Cancelar
              </Button>
            </DialogClose>
            <Button
              className="w-32 bg-black-1 text-white px-4 py-3 text-base text-center"
              onClick={() => {
                handleUploadFile()
                setOpen(false)
              }}
              disabled={!processData.file}
            >
              Subir
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
